import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft, faChevronRight, faChevronDown, faLightbulb,
  faGraduationCap, faChalkboardTeacher, faCalendarAlt,
  faPlus, faEdit, faTrash, faUserPlus, faUserMinus,
  faImage, faTag, faClock, faUsers, faDollarSign,
  faList, faEye, faSave, faCheckCircle, faExclamationTriangle,
  faBook, faUser, faCalendarCheck, faClock as faClockIcon,
  faUserShield
} from '@fortawesome/free-solid-svg-icons';

const AdminTutoFormations = () => {
  const [expandedSection, setExpandedSection] = useState(null);

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const sections = [
    {
      id: 'general',
      icon: faGraduationCap,
      title: '1. Présentation Générale',
      color: '#3b82f6',
      intro: 'Ce module permet de gérer l\'intégralité du cycle de vie des formations : création, planification, assignation des formateurs et gestion des sessions.',
      steps: [
        {
          step: 'a',
          title: 'Qu\'est-ce qu\'une formation ?',
          description: 'Une formation est un programme éducatif ou professionnel destiné aux employés. Exemples : "Excel avancé", "Management d\'équipe", "Sécurité incendie".',
          details: [
            '📚 Titre : Nom de la formation',
            '📝 Description : Objectifs, contenu, prérequis, public visé',
            '🏷️ Catégorie : Thème ou domaine (Technique, Management, Langues, Sécurité)',
            '⏰ Durée : Durée totale (ex: 2 jours, 14 heures, 1 mois)',
            '👥 Participants : Nombre maximum de participants',
            '💰 Prix : Coût de la formation (en devise locale ou USD)',
            '🖼️ Image : Illustration ou logo (optionnel)'
          ],
          tip: '💡 Astuce : Une formation peut être organisée en plusieurs sessions (ex: session de janvier, session de mars) avec des dates différentes.'
        },
        {
          step: 'b',
          title: 'Qu\'est-ce qu\'un formateur ?',
          description: 'Un formateur est un employé (ou un intervenant externe) chargé d\'animer la formation. Un même employé peut être formateur sur plusieurs formations.',
          details: [
            '👤 Un formateur est assigné à une formation spécifique',
            '📋 Une formation peut avoir plusieurs formateurs',
            '🔄 Un formateur peut être assigné à plusieurs formations'
          ],
          tip: '💡 Astuce : Vérifiez la disponibilité des formateurs avant de planifier des sessions.'
        },
        {
          step: 'c',
          title: 'Qu\'est-ce qu\'une session ?',
          description: 'Une session est une occurrence concrète de la formation avec des dates précises. Par exemple : la formation "Excel avancé" peut avoir une session du 15 au 20 mai et une autre du 10 au 15 juin.',
          details: [
            '📅 Mois : Période concernée (ex: "Mai 2025")',
            '📆 Date de début : Premier jour de la session',
            '📆 Date de fin : Dernier jour de la session',
            '🆕 La date de création (created_at) est automatiquement renseignée'
          ],
          tip: '💡 Astuce : Créez plusieurs sessions à l\'avance pour permettre aux employés de choisir la période qui leur convient.'
        }
      ]
    },
    {
      id: 'acces',
      icon: faList,
      title: '2. Accès à la Gestion des Formations',
      color: '#10b981',
      intro: 'Cette section vous guide pour accéder à la page principale de gestion des formations et consulter le catalogue existant.',
      steps: [
        {
          step: 'a',
          title: 'Accéder à la page "Gestion des formations"',
          description: 'Dans le menu latéral, repérez la rubrique "Formations" et suivez ces étapes :',
          details: [
            '📋 Cliquez sur le sous-menu "Liste des formations" (ou "Gestion des formations")',
            '📊 Vous arrivez sur une page affichant toutes les formations existantes',
            '📋 Colonnes visibles : Titre, Catégorie, Prix, Image, Actions',
            '🔍 Utilisez la barre de recherche pour filtrer les formations par titre ou catégorie'
          ],
          tip: '💡 Astuce : Les formations sont triées par date de création décroissante.'
        },
        {
          step: 'b',
          title: 'Interface de gestion',
          description: 'La page principale offre plusieurs fonctionnalités :',
          details: [
            '🔵 Bouton "Ajouter une formation" pour créer une nouvelle formation',
            '✏️ Bouton "Modifier" (icône crayon) sur chaque ligne pour modifier une formation',
            '👤 Bouton "Assigner un formateur" pour gérer les formateurs',
            '📅 Bouton "Gérer les sessions" pour planifier les occurrences',
            '👁️ Bouton "Voir" (icône œil) pour consulter la fiche détaillée'
          ],
          tip: '💡 Astuce : Les actions sont disponibles dans la colonne "Actions" de chaque ligne.'
        }
      ]
    },
    {
      id: 'creation',
      icon: faPlus,
      title: '3. Créer et Modifier une Formation',
      color: '#8b5cf6',
      intro: 'Cette section explique comment créer une nouvelle formation ou modifier une formation existante.',
      steps: [
        {
          step: 'a',
          title: 'Créer une nouvelle formation',
          description: 'Pour ajouter une formation au catalogue :',
          details: [
            '🔵 Cliquez sur "Ajouter une formation" (en haut à droite ou en bas de la liste)',
            '📝 Un formulaire s\'ouvre avec les champs suivants :',
            '   📚 Titre (obligatoire) : Nom de la formation',
            '   📝 Description : Détail du contenu, objectifs, prérequis',
            '   🏷️ Catégorie : Thème ou domaine (Technique, Management, Langues, Sécurité)',
            '   ⏰ Durée : Durée totale (ex: 2 jours, 14 heures)',
            '   👥 Participants : Nombre maximum de participants',
            '   💰 Prix : Coût de la formation (0 si gratuite)',
            '   🖼️ Image (optionnel) : Téléchargez une illustration (JPG, PNG)',
            '💾 Cliquez sur "Enregistrer" pour valider'
          ],
          tip: '💡 Astuce : Utilisez une image représentative pour faciliter l\'identification dans le catalogue.'
        },
        {
          step: 'b',
          title: 'Modifier une formation existante',
          description: 'Pour mettre à jour les informations d\'une formation :',
          details: [
            '🔍 Dans la liste, repérez la formation concernée',
            '✏️ Cliquez sur "Modifier" (icône crayon) dans la colonne Actions',
            '📝 Le formulaire s\'ouvre, pré-rempli avec les données actuelles',
            '✏️ Apportez les modifications souhaitées (prix, description, etc.)',
            '💾 Cliquez sur "Enregistrer" pour valider'
          ],
          tip: '💡 Astuce : La modification est conservée dans l\'historique (champ updated_at mis à jour automatiquement).'
        },
        {
          step: 'c',
          title: 'Supprimer une formation (désactivation)',
          description: 'Pour retirer une formation du catalogue :',
          details: [
            '🔍 Repérez la formation concernée dans la liste',
            '🗑️ Cliquez sur "Supprimer" (icône poubelle) dans la colonne Actions',
            '⚠️ Une confirmation vous est demandée avant la suppression définitive'
          ],
          warning: '⚠️ Attention : La suppression d\'une formation entraîne également la suppression des sessions et assignations associées.'
        }
      ]
    },
    {
      id: 'formateurs',
      icon: faChalkboardTeacher,
      title: '4. Assignation des Formateurs',
      color: '#f59e0b',
      intro: 'Cette section explique comment assigner un ou plusieurs formateurs à une formation.',
      steps: [
        {
          step: 'a',
          title: 'Accéder à la section d\'assignation',
          description: 'Depuis la liste des formations, plusieurs méthodes sont possibles :',
          details: [
            '👤 Cliquez sur "Assigner un formateur" dans la colonne Actions',
            '📋 Ou cliquez sur le nom de la formation pour ouvrir sa fiche, puis allez dans l\'onglet "Formateurs"'
          ],
          tip: '💡 Astuce : Vous arrivez sur une page ou une modale dédiée aux assignations.'
        },
        {
          step: 'b',
          title: 'Assigner un formateur',
          description: 'Pour ajouter un formateur à une formation :',
          details: [
            '🔍 Utilisez le champ de recherche pour trouver un employé (nom ou matricule)',
            '👤 Sélectionnez l\'employé dans la liste déroulante',
            '📌 Cliquez sur "Assigner" (ou "Ajouter")',
            '✅ L\'employé devient formateur pour cette formation',
            '📋 Il apparaît dans la liste des formateurs associés'
          ],
          tip: '💡 Astuce : Un même employé peut être formateur sur plusieurs formations.'
        },
        {
          step: 'c',
          title: 'Modifier ou supprimer une assignation',
          description: 'Pour gérer les formateurs assignés :',
          details: [
            '🗑️ Cliquez sur "Retirer" pour supprimer un formateur de la formation',
            '🔄 Pour changer de formateur, retirez l\'ancien puis assignez le nouveau',
            '📋 Une formation peut avoir plusieurs formateurs si nécessaire'
          ],
          tip: '💡 Astuce : Vérifiez que les formateurs assignés sont disponibles aux dates des sessions.'
        }
      ]
    },
    {
      id: 'sessions',
      icon: faCalendarAlt,
      title: '5. Gestion des Sessions',
      color: '#ec4899',
      intro: 'Cette section explique comment créer, modifier et supprimer des sessions (périodes) pour une formation.',
      steps: [
        {
          step: 'a',
          title: 'Accéder à la gestion des sessions',
          description: 'Depuis la fiche d\'une formation, gérez les sessions :',
          details: [
            '📅 Cliquez sur "Gérer les sessions" dans la colonne Actions',
            '📋 Ou ouvrez la fiche de la formation et allez dans l\'onglet "Sessions"',
            '📊 Une liste des sessions existantes s\'affiche'
          ],
          tip: '💡 Astuce : Les sessions sont triées par date de début croissante.'
        },
        {
          step: 'b',
          title: 'Ajouter une nouvelle session',
          description: 'Pour créer une occurrence de formation :',
          details: [
            '➕ Cliquez sur "Ajouter une session"',
            '📝 Un formulaire s\'ouvre avec les champs :',
            '   📅 Mois : Saisissez le mois concerné (ex: "Mai 2025") – champ libre',
            '   📆 Date de début : Choisissez le premier jour de la session',
            '   📆 Date de fin : Choisissez le dernier jour de la session',
            '💾 Cliquez sur "Enregistrer" pour valider'
          ],
          tip: '💡 Astuce : La date de création (created_at) est automatiquement renseignée.'
        },
        {
          step: 'c',
          title: 'Modifier ou supprimer une session',
          description: 'Pour gérer les sessions existantes :',
          details: [
            '✏️ Cliquez sur l\'icône crayon pour modifier le mois, la date de début ou de fin',
            '🗑️ Cliquez sur l\'icône poubelle pour supprimer une session',
            '⚠️ La suppression est définitive : les inscriptions éventuelles doivent être gérées séparément'
          ],
          warning: '⚠️ Attention : La suppression d\'une session est définitive. Les inscriptions liées à cette session devront être recréées.'
        }
      ]
    }
  ];

  return (
    <>
      <style>{`
        /* ============================================================
           STYLES – ADMIN TUTO FORMATIONS
           ============================================================ */
        :root {
          --bg-gradient: radial-gradient(circle at 10% 20%, #0f172a, #020617);
          --card-bg: rgba(255,255,255,0.04);
          --card-border: rgba(255,255,255,0.06);
          --text-primary: #e2e8f0;
          --text-secondary: #94a3b8;
          --text-muted: #64748b;
          --accent-blue: #3b82f6;
          --accent-green: #10b981;
          --accent-amber: #f59e0b;
          --accent-purple: #8b5cf6;
          --accent-red: #ef4444;
          --accent-cyan: #06b6d4;
          --accent-pink: #ec4899;
          --radius: 20px;
          --shadow: 0 20px 60px rgba(0,0,0,0.6);
        }

        .tuto-container {
          background: var(--bg-gradient);
          min-height: 100vh;
          padding: 2rem 1.5rem;
          color: var(--text-primary);
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          line-height: 1.6;
        }

        .tuto-header {
          display: flex;
          align-items: center;
          gap: 1rem;
          margin-bottom: 2rem;
          padding-bottom: 1.5rem;
          border-bottom: 1px solid rgba(255,255,255,0.04);
        }

        .tuto-header .back-link {
          color: var(--text-secondary);
          text-decoration: none;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 1rem;
          border-radius: 30px;
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.06);
          transition: 0.2s;
          font-size: 0.9rem;
        }
        .tuto-header .back-link:hover {
          background: rgba(255,255,255,0.08);
          color: #fff;
        }

        .tuto-header h1 {
          font-size: clamp(1.8rem, 4vw, 2.8rem);
          font-weight: 700;
          background: linear-gradient(135deg, #60a5fa, #06b6d4);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          margin: 0;
        }

        .tuto-header .subtitle {
          color: var(--text-secondary);
          font-size: clamp(0.9rem, 1.2vw, 1.1rem);
          margin-top: 0.2rem;
        }

        .tuto-intro {
          background: rgba(255,255,255,0.02);
          border: 1px solid rgba(255,255,255,0.04);
          border-radius: var(--radius);
          padding: 1.5rem 2rem;
          margin-bottom: 2rem;
        }
        .tuto-intro .badge-roles {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
          margin-top: 0.5rem;
        }
        .tuto-intro .badge-roles span {
          padding: 0.25rem 0.8rem;
          border-radius: 30px;
          font-size: 0.75rem;
          font-weight: 500;
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.06);
          color: var(--text-secondary);
        }
        .tuto-intro .badge-roles .admin { background: rgba(244,63,94,0.15); color: #f43f5e; border-color: rgba(244,63,94,0.15); }
        .tuto-intro .badge-roles .dsi { background: rgba(6,182,212,0.15); color: #06b6d4; border-color: rgba(6,182,212,0.15); }
        .tuto-intro .badge-roles .formateur { background: rgba(16,185,129,0.15); color: #10b981; border-color: rgba(16,185,129,0.15); }

        .section-card {
          background: rgba(255,255,255,0.02);
          border: 1px solid rgba(255,255,255,0.04);
          border-radius: var(--radius);
          margin-bottom: 1.2rem;
          overflow: hidden;
          transition: 0.3s;
        }
        .section-card:hover {
          border-color: rgba(255,255,255,0.08);
        }

        .section-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 1.2rem 1.8rem;
          cursor: pointer;
          transition: 0.2s;
          background: rgba(255,255,255,0.01);
          user-select: none;
        }
        .section-header:hover {
          background: rgba(255,255,255,0.03);
        }
        .section-header .left {
          display: flex;
          align-items: center;
          gap: 1rem;
        }
        .section-header .left .icon {
          font-size: 1.5rem;
          width: 36px;
          text-align: center;
        }
        .section-header .left .title {
          font-size: clamp(1.1rem, 1.4vw, 1.3rem);
          font-weight: 600;
        }
        .section-header .left .badge {
          font-size: 0.65rem;
          padding: 0.15rem 0.6rem;
          border-radius: 30px;
          background: rgba(255,255,255,0.04);
          color: var(--text-muted);
        }
        .section-header .toggle-icon {
          color: var(--text-muted);
          transition: transform 0.3s;
          font-size: 0.9rem;
        }
        .section-header .toggle-icon.open {
          transform: rotate(180deg);
        }

        .section-content {
          padding: 0 1.8rem 1.8rem 1.8rem;
          border-top: 1px solid rgba(255,255,255,0.03);
          animation: slideDown 0.3s ease;
        }

        .section-intro {
          padding: 0.8rem 0 1rem 0;
          color: var(--text-secondary);
          font-style: italic;
          border-bottom: 1px solid rgba(255,255,255,0.03);
          margin-bottom: 1rem;
          font-size: 0.95rem;
        }
        .section-intro strong {
          color: var(--text-primary);
        }

        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .step-item {
          display: flex;
          gap: 1rem;
          padding: 0.8rem 0;
          border-bottom: 1px solid rgba(255,255,255,0.02);
        }
        .step-item:last-child {
          border-bottom: none;
        }
        .step-item .step-number {
          font-weight: 700;
          font-size: 1.1rem;
          min-width: 28px;
          color: var(--text-muted);
        }
        .step-item .step-content {
          flex: 1;
        }
        .step-item .step-content .step-title {
          font-weight: 600;
          color: #fff;
          margin-bottom: 0.2rem;
        }
        .step-item .step-content .step-desc {
          color: var(--text-secondary);
          font-size: 0.95rem;
        }
        .step-item .step-content .step-details {
          margin-top: 0.4rem;
          padding-left: 1.2rem;
        }
        .step-item .step-content .step-details li {
          color: var(--text-secondary);
          font-size: 0.9rem;
          list-style: none;
          padding: 0.1rem 0;
        }
        .step-item .step-content .step-details li::before {
          content: '• ';
          color: var(--accent-blue);
        }
        .step-item .step-content .step-tip {
          margin-top: 0.4rem;
          padding: 0.4rem 1rem;
          background: rgba(59,130,246,0.08);
          border-left: 3px solid var(--accent-blue);
          border-radius: 6px;
          font-size: 0.9rem;
          color: var(--text-secondary);
        }
        .step-item .step-content .step-warning {
          margin-top: 0.4rem;
          padding: 0.4rem 1rem;
          background: rgba(239,68,68,0.08);
          border-left: 3px solid var(--accent-red);
          border-radius: 6px;
          font-size: 0.9rem;
          color: #fca5a5;
        }

        .footer-tuto {
          text-align: center;
          padding-top: 2rem;
          border-top: 1px solid rgba(255,255,255,0.04);
          color: var(--text-secondary);
          font-size: 0.85rem;
        }

        @media (max-width: 768px) {
          .tuto-container { padding: 1.2rem; }
          .tuto-header { flex-direction: column; align-items: flex-start; }
          .section-header { padding: 1rem 1.2rem; flex-wrap: wrap; gap: 0.5rem; }
          .section-content { padding: 0 1.2rem 1.2rem 1.2rem; }
          .step-item { flex-direction: column; gap: 0.3rem; }
          .tuto-intro { padding: 1rem; }
        }
      `}</style>

      <div className="tuto-container">
        {/* ===== EN-TÊTE ===== */}
        <div className="tuto-header">
          <a href="/dashboard" className="back-link">
            <FontAwesomeIcon icon={faArrowLeft} /> Retour
          </a>
          <div>
            <h1>🎓 Tutoriel Formations</h1>
            <div className="subtitle">Bisenter ERP – Guide complet pour la gestion des formations, formateurs et sessions</div>
          </div>
        </div>

        {/* ===== INTRODUCTION ===== */}
        <div className="tuto-intro">
          <p style={{ color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
            Ce tutoriel couvre l'ensemble des fonctionnalités du module <strong style={{ color: '#fff' }}>Formations</strong> 
            de l'application Bisenter ERP. Il est destiné aux <strong style={{ color: '#fff' }}>Super Administrateurs</strong>, 
            aux <strong style={{ color: '#fff' }}>DSI</strong> et aux <strong style={{ color: '#fff' }}>Formateurs</strong>.
          </p>
          <div className="badge-roles">
            <span className="admin"><FontAwesomeIcon icon={faUserShield} /> Super Administrateur</span>
            <span className="dsi"><FontAwesomeIcon icon={faUser} /> DSI</span>
            <span className="formateur"><FontAwesomeIcon icon={faChalkboardTeacher} /> Formateur</span>
          </div>
        </div>

        {/* ===== SECTIONS ===== */}
        {sections.map((section) => (
          <div key={section.id} className="section-card">
            <div className="section-header" onClick={() => toggleSection(section.id)}>
              <div className="left">
                <span className="icon" style={{ color: section.color }}>
                  <FontAwesomeIcon icon={section.icon} />
                </span>
                <span className="title">{section.title}</span>
                <span className="badge">{section.steps.length} étapes</span>
              </div>
              <FontAwesomeIcon
                icon={expandedSection === section.id ? faChevronDown : faChevronRight}
                className={`toggle-icon ${expandedSection === section.id ? 'open' : ''}`}
              />
            </div>

            {expandedSection === section.id && (
              <div className="section-content">
                <div className="section-intro">
                  <strong>📖 À lire :</strong> {section.intro}
                </div>
                {section.steps.map((step, index) => (
                  <div key={index} className="step-item">
                    <div className="step-number">{step.step}.</div>
                    <div className="step-content">
                      <div className="step-title">{step.title}</div>
                      <div className="step-desc">{step.description}</div>
                      {step.details && (
                        <ul className="step-details">
                          {step.details.map((detail, idx) => (
                            <li key={idx}>{detail}</li>
                          ))}
                        </ul>
                      )}
                      {step.tip && (
                        <div className="step-tip">
                          <FontAwesomeIcon icon={faLightbulb} style={{ marginRight: '0.5rem', color: 'var(--accent-blue)' }} />
                          {step.tip}
                        </div>
                      )}
                      {step.warning && (
                        <div className="step-warning">
                          <FontAwesomeIcon icon={faExclamationTriangle} style={{ marginRight: '0.5rem', color: 'var(--accent-red)' }} />
                          {step.warning}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}

        {/* ===== RÉSUMÉ DES ACTIONS ===== */}
        <div className="glass-card" style={{
          background: 'rgba(255,255,255,0.02)',
          border: '1px solid rgba(59,130,246,0.15)',
          borderRadius: 'var(--radius)',
          padding: '1.5rem',
          marginTop: '1.5rem'
        }}>
          <h3 style={{ color: '#fff', marginBottom: '0.8rem' }}>
            <FontAwesomeIcon icon={faCheckCircle} style={{ color: 'var(--accent-green)', marginRight: '0.5rem' }} />
            Récapitulatif des actions
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
            <div style={{ background: 'rgba(255,255,255,0.02)', padding: '0.8rem', borderRadius: '12px' }}>
              <h4 style={{ color: '#3b82f6', marginBottom: '0.3rem' }}>Formations</h4>
              <ul style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', paddingLeft: '1.2rem' }}>
                <li>➕ Créer une formation</li>
                <li>✏️ Modifier une formation</li>
                <li>🗑️ Supprimer une formation</li>
                <li>👁️ Consulter une formation</li>
              </ul>
            </div>
            <div style={{ background: 'rgba(255,255,255,0.02)', padding: '0.8rem', borderRadius: '12px' }}>
              <h4 style={{ color: '#f59e0b', marginBottom: '0.3rem' }}>Formateurs</h4>
              <ul style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', paddingLeft: '1.2rem' }}>
                <li>👤 Assigner un formateur</li>
                <li>🗑️ Retirer un formateur</li>
                <li>🔄 Changer de formateur</li>
              </ul>
            </div>
            <div style={{ background: 'rgba(255,255,255,0.02)', padding: '0.8rem', borderRadius: '12px' }}>
              <h4 style={{ color: '#ec4899', marginBottom: '0.3rem' }}>Sessions</h4>
              <ul style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', paddingLeft: '1.2rem' }}>
                <li>➕ Créer une session</li>
                <li>✏️ Modifier une session</li>
                <li>🗑️ Supprimer une session</li>
              </ul>
            </div>
          </div>
        </div>

        {/* ===== CONSEILS PRATIQUES ===== */}
        <div className="glass-card" style={{
          background: 'rgba(255,255,255,0.02)',
          border: '1px solid rgba(16,185,129,0.15)',
          borderRadius: 'var(--radius)',
          padding: '1.5rem',
          marginTop: '1rem'
        }}>
          <h3 style={{ color: '#fff', marginBottom: '0.8rem' }}>
            <FontAwesomeIcon icon={faLightbulb} style={{ color: 'var(--accent-amber)', marginRight: '0.5rem' }} />
            Conseils pratiques
          </h3>
          <ul style={{ color: 'var(--text-secondary)', paddingLeft: '1.2rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '0.5rem' }}>
            <li style={{ listStyle: 'none', padding: '0.3rem 0' }}>
              <strong style={{ color: '#fff' }}>🖼️ Image :</strong> Utilisez une image représentative pour faciliter l'identification.
            </li>
            <li style={{ listStyle: 'none', padding: '0.3rem 0' }}>
              <strong style={{ color: '#fff' }}>💰 Prix :</strong> Si la formation est gratuite, mettez 0.00.
            </li>
            <li style={{ listStyle: 'none', padding: '0.3rem 0' }}>
              <strong style={{ color: '#fff' }}>👥 Participants :</strong> Fixez un nombre réaliste pour éviter les surcharges. Mettez 0 si illimité.
            </li>
            <li style={{ listStyle: 'none', padding: '0.3rem 0' }}>
              <strong style={{ color: '#fff' }}>📅 Sessions :</strong> Créez plusieurs sessions à l'avance pour permettre aux employés de choisir la période qui leur convient.
            </li>
            <li style={{ listStyle: 'none', padding: '0.3rem 0' }}>
              <strong style={{ color: '#fff' }}>👤 Formateurs :</strong> Vérifiez la disponibilité des formateurs avant de planifier les sessions.
            </li>
            <li style={{ listStyle: 'none', padding: '0.3rem 0' }}>
              <strong style={{ color: '#fff' }}>📋 Description :</strong> Soyez précis dans la description pour faciliter le choix des employés.
            </li>
          </ul>
        </div>

        {/* ===== PIED DE PAGE ===== */}
        <div className="footer-tuto">
          <p>
            <strong>Besoin d'aide ?</strong> Contactez le support technique à <span style={{ color: 'var(--accent-blue)' }}>support@bisenter.com</span>
          </p>
          <p style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: 'var(--text-muted)' }}>
            © {new Date().getFullYear()} Bisenter – Tous droits réservés.
          </p>
        </div>
      </div>
    </>
  );
};

export default AdminTutoFormations;